<script setup lang="ts">
  import { usePotaParksInitStore } from '@/stores/PotaParksInitStore'
  import { watch, ref, onUnmounted } from 'vue'

  const initStore = usePotaParksInitStore()
  const isVisible = ref(false)
  const autoHideTimeout = ref<NodeJS.Timeout | null>(null)

  // Show toast when initialization starts and auto-hide after completion
  watch(() => initStore.state, (newState) => {
    if (newState === 'checking' || newState === 'downloading' || newState === 'processing') {
      isVisible.value = true
    } else if (newState === 'completed') {
      autoHideTimeout.value = setTimeout(() => {
        isVisible.value = false
        initStore.reset()
      }, 3000) // Show success message for 3 seconds
    }
  })

  onUnmounted(() => {
    if (autoHideTimeout.value) {
      clearTimeout(autoHideTimeout.value)
    }
  })
</script>


<template>
  <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0" leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 translate-y-2">
    <div v-if="isVisible"
      class="pota-parks-init-toast fixed top-4 right-4 z-40 max-w-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4">
      <!-- Success State -->
      <div v-if="initStore.state === 'completed'" class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-gray-900 dark:text-white">
            Parks Database Ready
          </h3>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Park lookups are now optimized
          </p>
        </div>
      </div>

      <!-- Progress State -->
      <div v-else class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="w-5 h-5 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-medium text-gray-900 dark:text-white">
            Setting Up Parks Database
          </h3>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            One-time setup running in background
          </p>

          <!-- Progress bar for processing state -->
          <div v-if="initStore.state === 'processing' && initStore.totalParks > 0" class="mt-2">
            <div class="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{{ initStore.progressPercentage }}%</span>
              <span>{{ initStore.processedParks.toLocaleString() }} / {{ initStore.totalParks.toLocaleString() }}</span>
            </div>
            <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
              <div class="bg-blue-400 h-1.5 rounded-full transition-all duration-300"
                :style="{ width: `${initStore.progressPercentage}%` }"></div>
            </div>
          </div>
        </div>

        <!-- Dismiss button -->
        <button @click="isVisible = false"
          class="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  </Transition>
</template>